import { useEffect, useState, useCallback } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { KidAvatar } from "../Brand";
import { parentApi, errMsg } from "../../lib/api";
import { CATEGORIES } from "../../lib/constants";

const Toggle = ({ id, label, hint, checked, onChange }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b-2 border-dashed border-[#1B1A2E]/20">
    <div><p className="font-semibold">{label}</p><p className="text-sm text-[#1B1A2E]/60">{hint}</p></div>
    <Switch data-testid={id} checked={checked} onCheckedChange={onChange} />
  </div>
);

export const KidControls = ({ kids, onChange }) => {
  const [kidId, setKidId] = useState(kids[0]?.id);
  const kid = kids.find((k) => k.id === kidId) || kids[0];
  const [s, setS] = useState(kid?.settings);

  useEffect(() => { setS(kid?.settings); }, [kid?.id, kid?.settings]);
  if (!kid || !s) return <p data-testid="controls-no-kids" className="font-display text-xl">Add a kid profile first.</p>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const toggleCat = (c) => set("allowed_categories", s.allowed_categories.includes(c) ? s.allowed_categories.filter((x) => x !== c) : [...s.allowed_categories, c]);

  const save = async () => {
    try {
      await parentApi.patch(`/kids/${kid.id}`, { settings: s });
      toast.success(`Controls saved for ${kid.name}`);
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold">Controls</h1>
      <div className="flex gap-3 flex-wrap">
        {kids.map((k) => (
          <button key={k.id} data-testid={`controls-kid-${k.id}`} onClick={() => setKidId(k.id)} className={`zn-chip ${k.id === kid.id ? "bg-[#FFD23F]" : "bg-white"}`}>
            <KidAvatar avatar={k.avatar} color={k.color} size={26} className="!border-2" /> {k.name}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="zn-card p-6 bg-white">
          <h2 className="text-2xl font-bold mb-1">Daily screen time</h2>
          <p className="text-sm text-[#1B1A2E]/60 mb-6">Zunii shows a friendly "time for a break" screen when it runs out.</p>
          <p data-testid="controls-limit-value" className="font-display text-5xl font-bold mb-4">{s.daily_limit_minutes}<span className="text-xl"> min</span></p>
          <Slider data-testid="controls-limit-slider" min={5} max={180} step={5} value={[s.daily_limit_minutes]} onValueChange={([v]) => set("daily_limit_minutes", v)} />
          <p className="text-sm mt-3">Used today: <b>{kid.minutes_used_today} min</b></p>
          <div className="mt-6 pt-5 border-t-2 border-dashed border-[#1B1A2E]/20">
            <h3 className="font-display text-xl font-bold mb-1">Daily game time</h3>
            <p className="text-sm text-[#1B1A2E]/60 mb-4">Separate from video time. Games pause when it runs out.</p>
            <p data-testid="controls-game-limit-value" className="font-display text-4xl font-bold mb-4">{s.game_limit_minutes ?? 20}<span className="text-lg"> min</span></p>
            <Slider data-testid="controls-game-limit-slider" min={5} max={120} step={5} value={[s.game_limit_minutes ?? 20]} onValueChange={([v]) => set("game_limit_minutes", v)} />
          </div>
        </div>
        <div className="zn-card p-6 bg-white">
          <h2 className="text-2xl font-bold mb-2">Permissions</h2>
          <Toggle id="controls-uploads-switch" label="Can upload & record" hint="Videos still need your approval" checked={s.uploads_enabled} onChange={(v) => set("uploads_enabled", v)} />
          <Toggle id="controls-comments-switch" label="Can write comments" hint="Comments still need your approval" checked={s.comments_enabled} onChange={(v) => set("comments_enabled", v)} />
          <Toggle id="controls-family-switch" label="Family-only feed" hint="Only see videos from siblings in this family" checked={s.community_scope === "family"} onChange={(v) => set("community_scope", v ? "family" : "everyone")} />
        </div>
      </div>
      <div className="zn-card p-6 bg-white">
        <h2 className="text-2xl font-bold mb-1">Content filters</h2>
        <p className="text-sm text-[#1B1A2E]/60 mb-4">Choose which categories {kid.name} can watch and post in.</p>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(({ id, label, icon: Icon, color }) => (
            <button key={id} data-testid={`controls-category-${id}`} onClick={() => toggleCat(id)} className={`zn-chip ${s.allowed_categories.includes(id) ? "" : "opacity-40 line-through"}`} style={{ background: s.allowed_categories.includes(id) ? color : "#fff" }}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </div>
      <BlockedList kidId={kid.id} name={kid.name} />
      <button data-testid="controls-save-btn" onClick={save} className="zn-btn bg-[#7B5CFA] text-white"><Save size={18} /> Save controls</button>
    </div>
  );
};

const BlockedList = ({ kidId, name }) => {
  const [list, setList] = useState([]);
  const load = useCallback(() => parentApi.get(`/kids/${kidId}/blocks`).then((r) => setList(r.data)), [kidId]);
  useEffect(() => { load(); }, [load]);
  const unblock = async (c) => {
    await parentApi.delete(`/kids/${kidId}/blocks/${c.id}`);
    toast.success(`${c.name} unblocked for ${name}`);
    load();
  };
  return (
    <div className="zn-card p-6 bg-white" data-testid="blocked-list">
      <h2 className="text-2xl font-bold mb-1">Blocked creators</h2>
      <p className="text-sm text-[#1B1A2E]/60 mb-4">{name} won't see videos or comments from these creators.</p>
      {list.length === 0 && <p className="text-sm">Nobody is blocked.</p>}
      <div className="flex flex-wrap gap-3">
        {list.map((c) => (
          <span key={c.id} className="zn-chip bg-white !cursor-default">
            <KidAvatar avatar={c.avatar} color={c.color} size={24} className="!border-2" /> {c.name}
            <button data-testid={`unblock-${c.id}`} onClick={() => unblock(c)} className="ml-1 underline text-xs">Unblock</button>
          </span>
        ))}
      </div>
    </div>
  );
};
